import React, { useState, useEffect } from 'react';
import eLibraryService from '../services/eLibraryService';
import categoryService from '../services/categoryService';
import { ELibrary, Category } from '../types/contentManagement';
import ContentModal from './ContentModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const EProcedureManagement: React.FC = () => {
  const [libraries, setLibraries] = useState<ELibrary[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingLibrary, setEditingLibrary] = useState<Partial<ELibrary> | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [libraryToDelete, setLibraryToDelete] = useState<ELibrary | null>(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);


  // Helper function to convert dd-mm-yyyy to yyyy-mm-dd for input field
  const convertToInputFormat = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    // If already in yyyy-mm-dd format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    // Convert dd-mm-yyyy to yyyy-mm-dd
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  // Helper function to convert yyyy-mm-dd to dd-mm-yyyy for API
  const convertToApiFormat = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    // If already in dd-mm-yyyy format, return as is
    if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr;
    // Convert yyyy-mm-dd to dd-mm-yyyy
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  useEffect(() => {
    fetchLibraries();
    fetchCategories();
  }, [filterCategory]);

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getCategoriesByType('library');
      setCategories(data.filter(cat => cat.is_active));
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchLibraries = async () => {
    try {
      setLoading(true);
      const data = filterCategory 
        ? await eLibraryService.getLibrariesByCategory(filterCategory, true)
        : await eLibraryService.getAllLibraries(true);
      // Sort by title in ascending order
      const sortedData = (data || []).sort((a, b) => 
        a.title.localeCompare(b.title)
      );
      setLibraries(sortedData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch e-library items');
      console.error('Error fetching library items:', err);
      setLibraries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setEditingLibrary({
      title: '',
      category: categories.length > 0 ? categories[0].name : '',
      date: new Date().toISOString().split('T')[0],
      is_visible: true,
      sort_order: libraries.length + 1
    });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (library: ELibrary) => {
    setEditingLibrary({ 
      ...library,
      date: convertToInputFormat(library.date) // Convert date for input field
    });
    setSelectedFile(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingLibrary(null);
    setSelectedFile(null);
  };

  const handleSave = async () => {
    if (!editingLibrary || !editingLibrary.title || !editingLibrary.category) {
      setSaveMessage('Error: Title and Category are required');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    if (!selectedFile && !editingLibrary.file_path && !editingLibrary.link) {
      setSaveMessage('Error: Please provide either a file or a link');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      
      formData.append('title', editingLibrary.title);
      formData.append('category', editingLibrary.category);
      if (editingLibrary.link) formData.append('link', editingLibrary.link);
      formData.append('date', editingLibrary.date || new Date().toISOString().split('T')[0]);
      formData.append('is_visible', String(editingLibrary.is_visible ?? true));
      formData.append('sort_order', String(editingLibrary.sort_order || libraries.length + 1));
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      if (editingLibrary.id) {
        await eLibraryService.updateLibrary(editingLibrary.id, formData);
        setSaveMessage('E-Library item saved successfully');
      } else {
        await eLibraryService.createLibrary(formData);
        setSaveMessage('E-Library item created successfully');
      }
      
      await fetchLibraries();
      handleCloseModal();
      setCurrentPage(1);
      
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error saving e-library item');
      console.error('Error saving library item:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (library: ELibrary) => {
    setLibraryToDelete(library);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!libraryToDelete) return;

    try {
      setSubmitting(true);
      await eLibraryService.deleteLibrary(libraryToDelete.id);
      await fetchLibraries();
      setCurrentPage(1);
      setSaveMessage('E-Library item deleted successfully');
      setTimeout(() => setSaveMessage(''), 3000);
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      setSaveMessage('Error deleting e-library item');
      console.error('Error deleting library item:', err);
      setTimeout(() => setSaveMessage(''), 5000);
    } finally {
      setSubmitting(false);
      setLibraryToDelete(null);
    }
  };

  const handleToggleVisibility = async (id: number) => {
    try {
      await eLibraryService.toggleVisibility(id);
      await fetchLibraries();
      window.dispatchEvent(new Event('contentUpdated'));
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  };

  // Filter libraries based on search query
  const filteredLibraries = libraries.filter(library => {
    const query = searchQuery.toLowerCase();
    return library.title.toLowerCase().includes(query);
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLibraries = filteredLibraries.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLibraries.length / itemsPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading e-procedures...</div>;
  }

  return (
    <div className="edit-section">
      <div className="applications-list">
        <div className="list-header">
          <h4>E-Procedure Management</h4>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select 
              value={filterCategory} 
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer' }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            <button 
              className="btn-add" 
              onClick={handleAddClick}
              disabled={submitting}
            >
              <i className="icon-plus"></i>
              Add E-Procedure
            </button>
          </div>
        </div>

        {saveMessage && (
          <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`} style={{
            padding: '12px',
            marginBottom: '20px',
            borderRadius: '4px',
            backgroundColor: saveMessage.includes('Error') ? '#f8d7da' : '#d4edda',
            color: saveMessage.includes('Error') ? '#721c24' : '#155724',
            border: `1px solid ${saveMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`,
            position: 'relative'
          }}>
            {saveMessage}
            <button
              onClick={() => setSaveMessage('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '8px',
                background: 'none',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: saveMessage.includes('Error') ? '#721c24' : '#155724',
                padding: '0',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close message"
            >
              ×
            </button>
          </div>
        )}

        {error && <div style={{ padding: '12px', marginBottom: '20px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>{error}</div>}

        {/* Search Bar */}
        <div style={{ marginTop: '20px', marginBottom: '15px' }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search by title..."
              value={searchQuery}
              onChange={handleSearchChange}
              style={{
                paddingLeft: '35px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                width: '100%',
                padding: '8px 8px 8px 35px'
              }}
            />
            <i 
              className="fas fa-search" 
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d'
              }}
            ></i>
          </div>
          {searchQuery && (
            <div style={{ marginTop: '8px', fontSize: '14px', color: '#6c757d' }}>
              Found {filteredLibraries.length} item{filteredLibraries.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div className="applications-table" style={{ marginTop: '20px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', minWidth: '900px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '250px' }}>Title</th>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '200px' }}>Category</th>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '120px' }}>Type</th>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #ddd', fontWeight: 'bold', width: '120px' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '80px' }}>Visible</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '80px' }}>Order</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #ddd', fontWeight: 'bold', width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentLibraries.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#999', border: '1px solid #ddd' }}>
                    {searchQuery ? 'No e-library items found matching your search' : 'No e-library items found. Click "Add E-Library Item" to create one.'}
                  </td>
                </tr>
              ) : (
                currentLibraries.map((library: ELibrary) => (
                  <tr 
                    key={library.id}
                    style={{ 
                      borderBottom: '1px solid #ddd'
                    }}
                  >
                    <td style={{ padding: '12px', border: '1px solid #ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <strong>{library.title}</strong>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <span style={{ 
                        backgroundColor: categories.find(c => c.name === library.category)?.color || '#95a5a6',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        whiteSpace: 'nowrap',
                        display: 'inline-block'
                      }}>
                        {library.category}
                      </span>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {library.file_path ? 'Document' : 'Link'}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {library.date}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={library.is_visible}
                        onChange={() => handleToggleVisibility(library.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {library.sort_order}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                        <button 
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            opacity: submitting ? 0.5 : 1
                          }}
                          onClick={() => handleEditClick(library)}
                          disabled={submitting}
                          title="Edit e-library item"
                          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = '#e3f2fd')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          ✏️
                        </button>
                        <button 
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '16px',
                            transition: 'all 0.3s ease',
                            opacity: submitting ? 0.5 : 1
                          }}
                          onClick={() => handleDeleteClick(library)}
                          disabled={submitting}
                          title="Delete e-library item"
                          onMouseEnter={(e) => !submitting && (e.currentTarget.style.background = '#ffebee')}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {filteredLibraries.length > itemsPerPage && (
            <div style={{ 
              marginTop: '20px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '10px 0'
            }}>
              <div style={{ fontSize: '14px', color: '#6c757d' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredLibraries.length)} of {filteredLibraries.length} e-library items
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                    color: currentPage === 1 ? '#6c757d' : '#007bff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      backgroundColor: currentPage === page ? '#007bff' : 'white',
                      color: currentPage === page ? 'white' : '#007bff',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: currentPage === page ? 'bold' : 'normal'
                    }}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ddd',
                    backgroundColor: currentPage === totalPages ? '#f8f9fa' : 'white',
                    color: currentPage === totalPages ? '#6c757d' : '#007bff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <ContentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingLibrary?.id ? 'Edit E-Library Item' : 'Add E-Library Item'}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Title <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              value={editingLibrary?.title || ''}
              onChange={(e) => setEditingLibrary((prev: any) => ({ ...prev, title: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="Enter e-library item title"
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Category <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              value={editingLibrary?.category || (categories.length > 0 ? categories[0].name : '')}
              onChange={(e) => setEditingLibrary((prev: any) => ({ ...prev, category: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              Document File {!editingLibrary?.link && <span style={{ color: 'red' }}>*</span>}
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
            {editingLibrary?.file_path && !selectedFile && (
              <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Current file: {editingLibrary.file_path.split('/').pop()}
              </p>
            )}
            {selectedFile && (
              <p style={{ fontSize: '12px', color: '#00a0e3', marginTop: '4px' }}>
                New file selected: {selectedFile.name}
              </p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>
              OR Link {!selectedFile && !editingLibrary?.file_path && <span style={{ color: 'red' }}>*</span>}
            </label>
            <input
              type="url"
              defaultValue={editingLibrary?.link || ''}
              onChange={(e) => setEditingLibrary((prev: any) => ({ ...prev, link: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              placeholder="https://example.com/library-item.pdf"
            />
            <p style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
              Provide either a file or a link
            </p>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500' }}>Date</label>
            <input
              type="date"
              value={editingLibrary?.date || ''}
              onChange={(e) => setEditingLibrary((prev: any) => ({ ...prev, date: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              id="is_visible"
              checked={editingLibrary?.is_visible ?? true}
              onChange={(e) => setEditingLibrary((prev: any) => ({ ...prev, is_visible: e.target.checked }))}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor="is_visible" style={{ cursor: 'pointer', fontWeight: '500' }}>
              Visible on homepage
            </label>
          </div>

          <div
            style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              marginTop: '10px',
              paddingTop: '16px',
              borderTop: '1px solid #ddd',
            }}
          >
            <button
              onClick={handleCloseModal}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#00a0e3',
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </ContentModal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete E-Procedure"
        message="Are you sure you want to delete this e-procedure?"
        itemName={libraryToDelete?.title}
      />
    </div>
  );
};

export default EProcedureManagement;
